import { useEffect, useState } from 'react';
import { paraNumero } from '@/leads/recebimentos';
import { mostrarBadgeOrcamento } from '@/leads/oportunidade';
import { atualizarLead } from '@/services/leadsService';

/**
 * VE — o campo "Valor de oportunidade" (o orçado; o CAMPO do banco segue `valorOrcado`), na ficha do lead.
 *
 * ★★ WD-c2 — ELE SÓ APARECE ENQUANTO O LEAD **NÃO FECHOU**. Depois de fechado, a oportunidade vira TEXTO no
 * bloco de leitura, e muda pelo botão "Editar" junto dos outros quatro valores (`CamposValores`).
 *
 * Por que não sumiu de vez, já que o bloco virou leitura: aqui é onde o número ENTRA. O orçamento quase
 * nunca sai na hora de marcar "compareceu" — vem da avaliação, minutos ou dias depois, e o lead fica em
 * "em negociação" esperando. Sem esta caixinha, um lead nessa situação não teria porta nenhuma: o diálogo
 * de valores exige lead FECHADO (`validarCorrecao` manda mudar a etapa), e o diálogo de etapa só abre ao
 * MUDAR de etapa. O pedido do dono no item 4 ("a caixinha da oportunidade alinhando com a do Negociador")
 * é sobre esta tela, e confirma que ela continua existindo.
 *
 * Existe porque o orçamento quase nunca sai na hora de marcar "compareceu": ele
 * vem da avaliação, minutos ou dias depois. O diálogo de etapa aceita em branco,
 * a Agenda nem pergunta — este é o lugar onde o número finalmente entra.
 *
 * Grava no BLUR, não a cada tecla: um `atualizarLead` por dígito escreveria
 * "1", "12", "120" no Firestore e carimbaria `valorOrcadoEm` no primeiro deles.
 *
 * ★★ WD — A ASSIMETRIA COM `valorFechado`/`valorEntrada` É DELIBERADA, NÃO ESQUECIMENTO.
 *
 * Este campo grava no BLUR, SEM motivo e SEM linha na timeline. Os outros dois valores de dinheiro
 * (`valorFechado` e `valorEntrada`) só mudam pelo diálogo `DialogoValores`, com MOTIVO OBRIGATÓRIO e de-para
 * na timeline. A razão é de uso, não de arquitetura: o valor ORÇADO é preenchido no meio do atendimento, às
 * vezes com o paciente na frente, e atrito ali atrapalha o trabalho — enquanto corrigir um valor FECHADO é
 * ato raro, sobre um número que já entrou em relatório.
 *
 * ★ Decisão do dono (24/set/2026). QUEM UNIFORMIZAR OS TRÊS ESTÁ DESFAZENDO UMA DECISÃO, não limpando uma
 * inconsistência. Há guarda de fonte (`web/src/leads/wd.guardas.test.js`) que falha se este campo passar a
 * exigir motivo — e que falha também se ESTE COMENTÁRIO for removido, porque sem ele a próxima pessoa
 * "conserta" o que não está quebrado.
 *
 * PROPS-ONLY de propósito: o painel da conversa (`/conversas`) vive fora do
 * `LeadsProvider`. Quem está dentro do provider repassa o que já tem.
 */
export default function CampoValorOrcado({ lead, clinicaId, autor, disabled = false, id = 'valor-orcado' }) {
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState(null);

  // Trocar de lead recarrega o campo: um valor pendurado do lead anterior seria
  // gravado no lead errado no próximo blur.
  useEffect(() => {
    setTexto(Number(lead?.valorOrcado) > 0 ? String(lead.valorOrcado) : '');
    setErro(null);
  }, [lead?.id, lead?.valorOrcado]);

  if (!lead) return null;

  const cobrar = mostrarBadgeOrcamento(lead);

  async function salvar() {
    const novo = Math.max(0, paraNumero(texto));
    if (novo === Number(lead.valorOrcado ?? 0)) return;
    setErro(null);
    try {
      await atualizarLead(
        clinicaId,
        lead.id,
        { valorOrcado: novo, registrarNaTimeline: false },
        autor,
      );
    } catch (e) {
      setErro(e.message ?? 'Não foi possível salvar o valor orçado.');
    }
  }

  return (
    <div className="campo">
      {/* ★ WD-c2 (item 4): o rótulo cabe em UMA LINHA — era o que desalinhava esta caixinha da do
          Negociador ao lado. É layout: a prova está no teste de navegador real, medindo. */}
      <label htmlFor={id}>
        Valor de oportunidade (R$)
        {cobrar && <span className="pilula-inferido alerta">informe</span>}
      </label>
      <input
        id={id}
        inputMode="decimal"
        placeholder="ex.: 12000"
        value={texto}
        disabled={disabled}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={salvar}
      />
      {erro && <p className="erro">{erro}</p>}
    </div>
  );
}
